import React, { useState, useRef, useEffect, useCallback } from "react";
import { obtenerTokenDeSesion } from "../../helpers/authToken";
import { Spinner } from "reactstrap";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { connect } from "react-redux";

const FUNCTIONS_URL = process.env.NODE_ENV === "development"
  ? "http://127.0.0.1:5001/ticketcontroldev/us-central1/api"
  : "https://us-central1-ticketcontroldev.cloudfunctions.net/api";

const aiChatStyles = `
.ai-chat-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.2); z-index: 1049; }
.ai-chat-panel {
  position: fixed; bottom: calc(24px + var(--banners-h, 0px)); right: 24px; z-index: 1050;
  width: calc(100vw - 100px); max-width: 1200px;
  max-height: calc(100vh - 48px);
  background: #fff; border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
  display: flex; flex-direction: column;
  overflow: hidden;
  animation: aiChatSlideUp 0.2s ease-out;
}
@media (max-width: 768px) {
  .ai-chat-panel {
    width: calc(100vw - 16px); right: 8px; bottom: calc(8px + var(--banners-h, 0px));
    max-height: calc(100vh - 16px);
    border-radius: 10px;
  }
}
@keyframes aiChatSlideUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}
.ai-chat-panel .ai-msg p { margin: 0 0 0.4em; }
.ai-chat-panel .ai-msg p:last-child { margin-bottom: 0; }
.ai-chat-panel .ai-msg h1, .ai-chat-panel .ai-msg h2, .ai-chat-panel .ai-msg h3 { font-size: 14px; font-weight: 700; margin: 0.6em 0 0.3em; }
.ai-chat-panel .ai-msg h1:first-child, .ai-chat-panel .ai-msg h2:first-child, .ai-chat-panel .ai-msg h3:first-child { margin-top: 0; }
.ai-chat-panel .ai-msg ul, .ai-chat-panel .ai-msg ol { margin: 0.3em 0; padding-left: 1.3em; }
.ai-chat-panel .ai-msg li { margin-bottom: 0.15em; }
.ai-chat-panel .ai-msg strong { font-weight: 600; }
.ai-chat-panel .ai-msg .ai-table-wrap { overflow-x: auto; margin: 0.5em 0; border-radius: 6px; border: 1px solid #dee2e6; }
.ai-chat-panel .ai-msg table { width: 100%; border-collapse: collapse; font-size: 12px; white-space: nowrap; }
.ai-chat-panel .ai-msg th, .ai-chat-panel .ai-msg td { border: 1px solid #dee2e6; padding: 5px 10px; text-align: left; }
.ai-chat-panel .ai-msg th { background: #556ee6; color: #fff; font-weight: 600; border-color: #4a62cc; }
.ai-chat-panel .ai-msg tr:nth-child(even) { background: #f8f9fa; }
.ai-chat-panel .ai-msg tr:hover { background: #e9ecef; }
.ai-chat-panel .ai-msg code { background: #e9ecef; padding: 1px 3px; border-radius: 3px; font-size: 12px; }
.ai-fab {
  position: fixed; bottom: calc(20px + var(--banners-h, 0px)); right: 24px; z-index: 1048;
  width: 48px; height: 48px; border-radius: 50%;
  background: linear-gradient(135deg, #556ee6, #34c38f);
  color: white; border: none;
  box-shadow: 0 4px 12px rgba(85,110,230,0.4);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: transform 0.15s, box-shadow 0.15s;
}
.ai-fab:hover { transform: scale(1.08); box-shadow: 0 6px 20px rgba(85,110,230,0.5); }
.ai-fab:active { transform: scale(0.95); }
`;

const MsgBubble = ({ msg }) => {
  const isUser = msg.role === "user";
  return (
    <div className={`d-flex ${isUser ? "justify-content-end" : "justify-content-start"} mb-2`}>
      {!isUser && (
        <div
          className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0 me-2"
          style={{ width: 28, height: 28, marginTop: 2 }}
        >
          <span className="material-symbols-rounded text-primary" style={{ fontSize: 15 }}>smart_toy</span>
        </div>
      )}
      <div
        className={`px-3 py-2 rounded-3 ${isUser ? "bg-primary text-white" : "bg-light border ai-msg"}`}
        style={{ maxWidth: "85%", fontSize: 13, lineHeight: 1.5, wordBreak: "break-word" }}
      >
        {isUser ? msg.text : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              table: ({ children }) => (
                <div className="ai-table-wrap"><table>{children}</table></div>
              ),
            }}
          >
            {msg.text}
          </ReactMarkdown>
        )}
      </div>
    </div>
  );
};

/**
 * Reusable AI Chat floating button + panel.
 *
 * Props:
 * - context: string     — Identifier for the section (e.g. "caja", "resumen_diario")
 * - dataSummary: string  — Text summary of the data visible in this section
 * - suggestions: string[] — Suggested questions for the user
 * - title: string         — Panel title (optional, defaults to "Asistente IA")
 * - dataScope: string     — Optional note about data scope (e.g. "120 registros cargados")
 */
const AIChatButton = ({ context, dataSummary, suggestions = [], title, dataScope, companyId }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const sendQuestion = useCallback(async (question) => {
    if (!question.trim() || loading) return;
    const userMsg = { role: "user", text: question.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      // El endpoint exige una sesion real de Firebase: gasta con nuestra clave
      // de Anthropic y su URL esta en el bundle publico. Sin token, 401.
      const token = await obtenerTokenDeSesion();
      const response = await fetch(`${FUNCTIONS_URL}/Monitor/ai-assistant`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-client-key": companyId,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          question: question.trim(),
          dataSummary,
          context,
        }),
      });
      const data = await response.json();
      if (data.success && data.answer) {
        setMessages((prev) => [...prev, { role: "assistant", text: data.answer }]);
      } else {
        setMessages((prev) => [...prev, { role: "assistant", text: data.error || "No pude procesar la consulta." }]);
      }
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", text: "Error de conexion. Intenta nuevamente." }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [loading, dataSummary, companyId, context]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendQuestion(input);
  };

  const isEmpty = messages.length === 0;

  return (
    <>
      <style>{aiChatStyles}</style>

      {/* FAB button */}
      <button className="ai-fab" onClick={() => setOpen(!open)} title="Asistente IA">
        <span className="material-symbols-rounded" style={{ fontSize: 24 }}>
          {open ? "close" : "auto_awesome"}
        </span>
      </button>

      {/* Chat panel */}
      {open && (
        <>
          <div className="ai-chat-overlay" onClick={() => setOpen(false)} />
          <div className="ai-chat-panel">
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom bg-light">
              <div className="d-flex align-items-center gap-2">
                <span className="material-symbols-rounded text-primary" style={{ fontSize: 20 }}>smart_toy</span>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{title || "Asistente IA"}</span>
              </div>
              <div className="d-flex align-items-center gap-1">
                <button
                  className="btn btn-sm btn-link text-muted p-0"
                  onClick={() => { setMessages([]); setInput(""); }}
                  title="Limpiar chat"
                  disabled={isEmpty && !input}
                >
                  <span className="material-symbols-rounded" style={{ fontSize: 18 }}>delete_sweep</span>
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-grow-1 overflow-auto p-3" style={{ minHeight: 200, maxHeight: "calc(100vh - 300px)" }}>
              {isEmpty ? (
                <div className="text-center py-3">
                  <span className="material-symbols-rounded text-primary d-block mb-2" style={{ fontSize: 36, opacity: 0.5 }}>
                    auto_awesome
                  </span>
                  <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                    Preguntame sobre los datos que ves en pantalla.
                  </p>
                  {dataScope && (
                    <p className="text-muted mb-3 d-flex align-items-center justify-content-center gap-1" style={{ fontSize: 11 }}>
                      <span className="material-symbols-rounded" style={{ fontSize: 14 }}>info</span>
                      {dataScope}
                    </p>
                  )}
                  {suggestions.length > 0 && (
                    <div className="d-flex flex-wrap justify-content-center gap-1">
                      {suggestions.map((q, i) => (
                        <button
                          key={i}
                          className="btn btn-outline-primary btn-sm rounded-pill"
                          style={{ fontSize: 12 }}
                          onClick={() => sendQuestion(q)}
                          disabled={loading}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {messages.map((msg, i) => (
                    <MsgBubble key={i} msg={msg} />
                  ))}
                  {loading && (
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div
                        className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: 28, height: 28 }}
                      >
                        <span className="material-symbols-rounded text-primary" style={{ fontSize: 15 }}>smart_toy</span>
                      </div>
                      <div className="bg-light border rounded-3 px-2 py-1">
                        <Spinner size="sm" color="primary" className="me-1" />
                        <span className="text-muted" style={{ fontSize: 12 }}>Analizando...</span>
                      </div>
                    </div>
                  )}
                  <div ref={endRef} />
                </>
              )}
            </div>

            {/* Input */}
            <div className="border-top px-3 py-2 bg-light">
              <form onSubmit={handleSubmit} className="d-flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Escribi tu pregunta..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={loading}
                  style={{ fontSize: 13 }}
                />
                <button
                  type="submit"
                  className="btn btn-primary btn-sm d-flex align-items-center px-2"
                  disabled={loading || !input.trim()}
                >
                  <span className="material-symbols-rounded" style={{ fontSize: 16 }}>send</span>
                </button>
              </form>
              {/* <div className="text-muted text-center mt-1" style={{ fontSize: 10 }}>
                Powered by Claude AI
              </div> */}
            </div>
          </div>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  companyId: state.companies.currentCompany?.id,
});

export default connect(mapStateToProps)(AIChatButton);
